import React from "react";
import MuiTypography, { TypographyProps as MuiTypographyProps } from "@material-ui/core/Typography";
import { useStyles } from "./styles";

export type TypographyProps<D extends React.ElementType = "span"> = MuiTypographyProps<D>;

/** Base Typography — applies Montserrat font by default. */
const Typography = React.forwardRef<HTMLElement, MuiTypographyProps>(
  ({ className, ...props }, ref) => {
    const classes = useStyles();
    return (
      <MuiTypography
        ref={ref}
        className={`${classes.base}${className ? ` ${className}` : ""}`}
        {...props}
      />
    );
  }
) as typeof MuiTypography & { displayName?: string };

Typography.displayName = "Typography";
export default Typography;

// ── Semantic preset components ─────────────────────────────────────────────

// Presets fix component="span"; omit it so consumers can't accidentally override the element.
type SpanPresetProps = Omit<MuiTypographyProps<"span">, "component">;

/** Uppercase muted label (e.g. field labels) */
export const FieldLabel = ({ className, ...props }: SpanPresetProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      component="span"
      className={`${classes.fieldLabel}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

/** Bold value text (e.g. field values) */
export const FieldValue = ({ className, ...props }: SpanPresetProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      component="span"
      className={`${classes.fieldValue}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

/** Section card heading */
export const SectionTitle = ({ className, ...props }: MuiTypographyProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      className={`${classes.sectionTitle}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

/** Section card description */
export const SectionSubtitle = ({ className, ...props }: MuiTypographyProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      className={`${classes.sectionSubtitle}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

/** Panel / drawer heading */
export const PanelTitle = ({ className, ...props }: MuiTypographyProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      className={`${classes.panelTitle}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};

/** Small helper text */
export const Caption = ({ className, ...props }: SpanPresetProps) => {
  const classes = useStyles();
  return (
    <MuiTypography
      component="span"
      className={`${classes.caption}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
};
